import { css } from '@emotion/react'
import { Button, Cell, DropdownItem, Grid, Heading, HFlow, Icon, Text, Tooltip, VFlow } from 'bold-ui'
import { FieldStatusEnum, Form, FormRenderProps, ListFieldStatus, PecFormApi } from 'components/form'
import { HLabel } from 'components/HLabel'
import { TextStatus } from 'components/text/TextStatus'
import { AnyObject } from 'final-form'
import { useState } from 'react'
import { noop } from 'util/functions'
import { metaPath } from 'util/metaPath'
import { getEditableItemRemoveTooltipText } from 'view/atendimentos/atendimento-individual/atendimento-observacao/util-atendObservacao'
import { EditableRow } from 'view/atendimentos/detail/components/EditableList'
import { Observacao } from 'view/atendimentos/detail/components/Observacao'
import { ObservacaoFormGrid } from 'view/atendimentos/detail/components/ObservacaoFormGrid'
import { removeWithOptionalConfirm } from 'view/retificacao-atendimento/removeWithOptionalConfirm'

import { CiapPlanoModel } from '../model-plano'
import { ciapPlanoValidator } from '../validator-plano'

const meta = metaPath<CiapPlanoModel>()

interface CiapRowProps {
  model: CiapPlanoModel
  onChange(values: CiapPlanoModel): void
  onRemove(itemToRemove: CiapPlanoModel): void
  onReset?: (itemToReset: CiapPlanoModel) => void
  status?: FieldStatusEnum
  controlModifiedStatus?: boolean
}

export function CiapRow(props: Readonly<CiapRowProps>) {
  const {
    model,
    onChange,
    onRemove,
    onReset = noop,
    status = FieldStatusEnum.NAO_MODIFICADO,
    controlModifiedStatus = false,
  } = props
  const [editing, setEditing] = useState(false)

  const { isEditable, tooltipText: removeTooltipText } = getEditableItemRemoveTooltipText(model)

  const editTooltipText = model.observacao ? 'Editar observação' : 'Adicionar observação'

  const toggleEditing = () => setEditing((oldState) => !oldState)

  const handleRemove = () =>
    removeWithOptionalConfirm({
      shouldAskConfirmation: controlModifiedStatus,
      title: 'Deseja excluir o campo CIAP2?',
      icon: { name: 'exclamationTriangleOutline', fill: 'alert' },
      onRemove: () => onRemove(model),
    })

  const handleReset = () => onReset(model)

  const handleSubmit = (value: CiapPlanoModel, formApi: PecFormApi<CiapPlanoModel>) => {
    onChange(value)
    setTimeout(formApi.reset)
    toggleEditing()
  }

  const renderForm = (formRenderProps: FormRenderProps<CiapPlanoModel>) => (
    <VFlow vSpacing={0.5} style={styles.observacaoFormWrapper}>
      <Heading level={4}>Adicionar observação</Heading>
      <HLabel title='CIAP 2:'>{model.ciap.descricao}</HLabel>
      <ObservacaoFormGrid name={meta.observacao} onCancel={toggleEditing} onSubmit={formRenderProps.handleSubmit} />
    </VFlow>
  )

  return (
    <EditableRow editing={editing}>
      {!editing && (
        <>
          <Grid style={styles.grid}>
            <Cell size={9} alignSelf='center'>
              <TextStatus status={status}>{`${model.ciap.descricao} - ${model.ciap.codigo}`}</TextStatus>
            </Cell>
            <Cell size={3} alignSelf='center' style={styles.actionsWrapper}>
              {controlModifiedStatus ? (
                <ListFieldStatus status={status} onReset={handleReset} onRemove={handleRemove}>
                  <DropdownItem onClick={toggleEditing}>
                    <HFlow alignItems='center' hSpacing={0.5}>
                      <Text variant='h4'>{editTooltipText}</Text>
                    </HFlow>
                  </DropdownItem>
                </ListFieldStatus>
              ) : (
                <>
                  <Tooltip text={editTooltipText}>
                    <Button size='small' skin='ghost' onClick={toggleEditing}>
                      <Icon icon='chatOutline' />
                    </Button>
                  </Tooltip>
                  <Tooltip text={removeTooltipText}>
                    <Button size='small' skin='ghost' onClick={handleRemove} disabled={isEditable}>
                      <Icon icon='trashOutline' />
                    </Button>
                  </Tooltip>
                </>
              )}
            </Cell>
          </Grid>
          <Observacao observacao={model.observacao} status={status} />
        </>
      )}
      {editing && (
        <Form<CiapPlanoModel>
          render={renderForm}
          initialValues={model}
          onSubmit={handleSubmit}
          validate={ciapPlanoValidator}
          initialValuesEqual={initialValuesEqual}
        />
      )}
    </EditableRow>
  )
}

const initialValuesEqual = (oldInitial: AnyObject | undefined, newInitial: AnyObject | undefined) =>
  oldInitial?._id === newInitial?._id

const styles = {
  actionsWrapper: css`
    padding: 0.25rem 0;
    text-align: right;
  `,
  observacaoFormWrapper: css`
    padding: 1rem 0;
  `,
  grid: css`
    margin: 0;
  `,
}
